import React, { useEffect, useMemo, useState } from "react";
import {
  Dialog, DialogContent, DialogActions, Box, Typography,
  TextField, Button, IconButton, Checkbox, InputAdornment, CircularProgress,
  Collapse, Chip,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import SearchIcon from "@mui/icons-material/Search";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ShieldOutlinedIcon from "@mui/icons-material/AdminPanelSettingsOutlined";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { getAccessRights, getRole, createRole, updateRole } from "app/api/access-rights.api";

const SLATE = "#3A4D54";

// Grouped, searchable permission picker — built so 100+ rights stay manageable
// as collapsible per-module sections rather than one flat list.
export default function RoleEditorDialog({ open, roleUuid, isSystem, onClose, onSaved }) {
  const isEdit = !!roleUuid;
  const [groups, setGroups] = useState([]);
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [selectedKeys, setSelectedKeys] = useState(new Set());
  const [search, setSearch] = useState("");
  const [collapsed, setCollapsed] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setIsLoading(true);
    setError("");
    setSearch("");
    setCollapsed({});
    (async () => {
      try {
        const rightsRes = await getAccessRights();
        const groupsData = rightsRes.data.data || [];
        let roleName = "";
        let roleDesc = "";
        let keys = new Set();
        if (isEdit) {
          const roleRes = await getRole(roleUuid);
          const role = roleRes.data.data;
          roleName = role.name || "";
          roleDesc = role.description || "";
          keys = new Set(role.access_keys || []);
        }
        if (cancelled) return;
        setGroups(groupsData);
        setName(roleName);
        setDescription(roleDesc);
        setSelectedKeys(keys);
      } catch (e) {
        if (!cancelled) setError(e.response?.data?.message || "Failed to load access rights");
      }
      if (!cancelled) setIsLoading(false);
    })();
    return () => { cancelled = true; };
  }, [open, roleUuid, isEdit]);

  const filteredGroups = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return groups;
    return groups
      .map((group) => ({
        ...group,
        rights: group.rights.filter((r) =>
          `${r.name} ${r.key} ${r.description || ""}`.toLowerCase().includes(query)
        ),
      }))
      .filter((group) => group.rights.length > 0);
  }, [groups, search]);

  const toggleKey = (key) => {
    setSelectedKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  };

  const toggleModule = (group, allSelected) => {
    setSelectedKeys((prev) => {
      const next = new Set(prev);
      group.rights.forEach((r) => { if (allSelected) next.delete(r.key); else next.add(r.key); });
      return next;
    });
  };

  const handleSave = async () => {
    setError("");
    if (!name.trim()) { setError("Role name is required."); return; }
    if (selectedKeys.size === 0) { setError("Select at least one access right."); return; }
    setIsSaving(true);
    const payload = {
      name: name.trim(),
      description: description.trim() || null,
      access_keys: Array.from(selectedKeys),
    };
    try {
      if (isEdit) await updateRole(roleUuid, payload);
      else await createRole(payload);
      onSaved();
    } catch (e) {
      setError(e.response?.data?.message || "Failed to save role");
    }
    setIsSaving(false);
  };

  const fieldLabel = { display: "block", fontSize: 13, fontWeight: 600, color: "#344054", marginBottom: 6 };

  return (
    <Dialog
      open={open}
      onClose={() => !isSaving && onClose()}
      maxWidth="sm"
      fullWidth
      PaperProps={{ style: { borderRadius: 16, overflow: "hidden" } }}
    >
      {/* Header */}
      <Box style={{ display: "flex", alignItems: "flex-start", gap: 14, padding: "22px 24px 18px", borderBottom: "1px solid #EEF1F2" }}>
        <Box style={{ width: 44, height: 44, borderRadius: 12, flex: "none", background: `linear-gradient(135deg, ${SLATE} 0%, #4E656E 100%)`, color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", boxShadow: "0 4px 10px rgba(58,77,84,0.25)" }}>
          <ShieldOutlinedIcon style={{ fontSize: 24 }} />
        </Box>
        <Box style={{ flex: 1, paddingTop: 2 }}>
          <Typography style={{ fontWeight: 700, fontSize: 17, color: "#101828", lineHeight: "24px" }}>
            {isEdit ? "Edit Role" : "New Role"}
          </Typography>
          <Typography style={{ fontSize: 13.5, color: "#667085", marginTop: 2 }}>
            Name the role and choose the access it grants to partner users.
          </Typography>
        </Box>
        <IconButton size="small" onClick={onClose} disabled={isSaving} style={{ marginTop: -4, marginRight: -6 }}>
          <CloseIcon style={{ fontSize: 18 }} />
        </IconButton>
      </Box>

      <DialogContent style={{ padding: "20px 24px", backgroundColor: "#FBFCFC" }}>
        {isLoading ? (
          <Box display="flex" justifyContent="center" py={5}><CircularProgress size={26} style={{ color: SLATE }} /></Box>
        ) : (
          <Box display="flex" flexDirection="column" gap={2.5}>
            {isSystem && (
              <Box style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 14px", borderRadius: 10, backgroundColor: "#EFF6FF", border: "1px solid #D3E4FD", color: "#1E5AA8" }}>
                <InfoOutlinedIcon style={{ fontSize: 18, flex: "none" }} />
                <Typography style={{ fontSize: 13, color: "inherit" }}>
                  This is a built-in role. You can adjust its rights, but it cannot be deleted.
                </Typography>
              </Box>
            )}

            <Box style={{ display: "flex", flexDirection: "column", gap: 16, padding: 16, backgroundColor: "#fff", border: "1px solid #EAECF0", borderRadius: 12 }}>
              <Box>
                <label style={fieldLabel}>Role name <span style={{ color: "#D92D20" }}>*</span></label>
                <TextField
                  fullWidth size="small" variant="outlined"
                  placeholder="e.g. Finance Operator"
                  value={name} onChange={(e) => setName(e.target.value)}
                />
              </Box>
              <Box>
                <label style={fieldLabel}>Description</label>
                <TextField
                  fullWidth size="small" variant="outlined"
                  placeholder="Optional — what this role is for"
                  value={description} onChange={(e) => setDescription(e.target.value)}
                />
              </Box>
            </Box>

            <Box>
              <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
                <label style={{ fontSize: 13, fontWeight: 600, color: "#344054" }}>
                  Access rights <span style={{ color: "#D92D20" }}>*</span>
                </label>
                <Chip
                  label={`${selectedKeys.size} selected`}
                  size="small"
                  style={{
                    fontSize: 12, fontWeight: 600, height: 24,
                    backgroundColor: selectedKeys.size ? "#EAF0F1" : "#F2F4F7",
                    color: selectedKeys.size ? SLATE : "#98A2B3",
                  }}
                />
              </Box>
              <TextField
                fullWidth size="small" variant="outlined"
                placeholder="Search access rights"
                value={search} onChange={(e) => setSearch(e.target.value)}
                InputProps={{
                  startAdornment: <InputAdornment position="start"><SearchIcon style={{ fontSize: 18, color: "#98A2B3" }} /></InputAdornment>,
                  style: { backgroundColor: "#fff" },
                }}
                style={{ marginBottom: 10 }}
              />
              <Box display="flex" flexDirection="column" gap={1.5}>
                {filteredGroups.length === 0 ? (
                  <Box style={{ padding: 24, textAlign: "center", color: "#98A2B3", fontSize: 13.5, backgroundColor: "#fff", border: "1px solid #EAECF0", borderRadius: 12 }}>
                    No access rights match your search
                  </Box>
                ) : (
                  filteredGroups.map((group) => {
                    const selectedInGroup = group.rights.filter((r) => selectedKeys.has(r.key)).length;
                    const allSelected = selectedInGroup === group.rights.length;
                    const anySelected = selectedInGroup > 0;
                    const isCollapsed = collapsed[group.module];
                    return (
                      <Box key={group.module} style={{ border: `1px solid ${anySelected ? "#CBD9DD" : "#EAECF0"}`, borderRadius: 12, overflow: "hidden", backgroundColor: "#fff", transition: "border-color 0.2s ease" }}>
                        <Box
                          display="flex" alignItems="center" justifyContent="space-between"
                          style={{ padding: "10px 14px", backgroundColor: anySelected ? "#F3F7F8" : "#FCFCFD", cursor: "pointer", transition: "background-color 0.2s ease" }}
                          onClick={() => setCollapsed((prev) => ({ ...prev, [group.module]: !prev[group.module] }))}
                        >
                          <Box display="flex" alignItems="center" gap={1}>
                            <Checkbox
                              size="small"
                              checked={allSelected}
                              indeterminate={anySelected && !allSelected}
                              onClick={(e) => { e.stopPropagation(); toggleModule(group, allSelected); }}
                              style={{ padding: 4, color: SLATE }}
                            />
                            <Typography style={{ fontSize: 13.5, fontWeight: 600, color: "#101828" }}>{group.module}</Typography>
                            <Chip
                              label={`${selectedInGroup}/${group.rights.length}`}
                              size="small"
                              style={{ height: 20, fontSize: 11, fontWeight: 600, backgroundColor: anySelected ? "#E3ECEE" : "#F2F4F7", color: anySelected ? SLATE : "#98A2B3" }}
                            />
                          </Box>
                          {isCollapsed ? <ExpandMoreIcon style={{ fontSize: 20, color: "#98A2B3" }} /> : <ExpandLessIcon style={{ fontSize: 20, color: "#98A2B3" }} />}
                        </Box>
                        <Collapse in={!isCollapsed} timeout="auto" unmountOnExit>
                          <Box>
                            {group.rights.map((right, idx) => {
                              const checked = selectedKeys.has(right.key);
                              return (
                                <Box
                                  key={right.key}
                                  display="flex" alignItems="flex-start" gap={1}
                                  style={{
                                    padding: "9px 14px 9px 17px",
                                    borderTop: "1px solid #F2F4F7",
                                    borderLeft: `3px solid ${checked ? SLATE : "transparent"}`,
                                    backgroundColor: checked ? "#F6F9FA" : "#fff",
                                    cursor: "pointer",
                                    transition: "background-color 0.15s ease, border-color 0.15s ease",
                                  }}
                                  onMouseEnter={(e) => { if (!checked) e.currentTarget.style.backgroundColor = "#F9FAFB"; }}
                                  onMouseLeave={(e) => { if (!checked) e.currentTarget.style.backgroundColor = "#fff"; }}
                                  onClick={() => toggleKey(right.key)}
                                >
                                  <Checkbox
                                    size="small"
                                    checked={checked}
                                    onClick={(e) => { e.stopPropagation(); toggleKey(right.key); }}
                                    style={{ padding: 4, color: SLATE }}
                                  />
                                  <Box>
                                    <Typography style={{ fontSize: 13.5, fontWeight: checked ? 600 : 500, color: "#101828" }}>{right.name}</Typography>
                                    {right.description && (
                                      <Typography style={{ fontSize: 12, color: "#98A2B3", marginTop: 1 }}>{right.description}</Typography>
                                    )}
                                  </Box>
                                </Box>
                              );
                            })}
                          </Box>
                        </Collapse>
                      </Box>
                    );
                  })
                )}
              </Box>
            </Box>

            {error && (
              <Box style={{ display: "flex", alignItems: "center", gap: 8, padding: "10px 14px", borderRadius: 10, backgroundColor: "#FEF3F2", border: "1px solid #FECDCA", color: "#B42318" }}>
                <InfoOutlinedIcon style={{ fontSize: 18, flex: "none" }} />
                <Typography style={{ fontSize: 13, color: "inherit" }}>{error}</Typography>
              </Box>
            )}
          </Box>
        )}
      </DialogContent>

      <DialogActions style={{ padding: "14px 24px", borderTop: "1px solid #EEF1F2", gap: 10 }}>
        <Button
          onClick={onClose} disabled={isSaving}
          style={{ color: "#344054", fontWeight: 600, fontSize: 14, textTransform: "none", border: "1px solid #D0D5DD", borderRadius: 8, padding: "7px 18px" }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          disabled={isSaving || isLoading}
          disableElevation
          style={{
            backgroundColor: isSaving || isLoading ? undefined : SLATE,
            color: isSaving || isLoading ? undefined : "#fff",
            fontWeight: 600, fontSize: 14, textTransform: "none", borderRadius: 8, padding: "7px 22px", minWidth: 140,
          }}
        >
          {isSaving ? <CircularProgress size={18} style={{ color: "#fff" }} /> : (isEdit ? "Save Changes" : "Create Role")}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
